import { useNavigate } from "react-router-dom";
import { Dropdown, cx } from "@/components/ui";
import { useSession } from "@/hooks/useSession";
import { useNotifications, useReadNotifications, useSync, useSyncStatus } from "@/lib/queries";
import { dateFR, relative, time } from "@/lib/format";
import { authClient } from "@/lib/auth-client";
import { queryClient } from "@/lib/query-client";

export function Freshness() {
  const status = useSyncStatus();
  const sync = useSync();
  const { can } = useSession();
  const last = status.data?.lastSyncAt;
  const label = sync.isPending
    ? "Synchronisation…"
    : last
      ? new Date(last).toDateString() === new Date().toDateString()
        ? `Relevé à ${time(last)}`
        : `Relevé du ${dateFR(last)}`
      : "Jamais synchronisé";
  return (
    <div className="flex items-center gap-2 whitespace-nowrap px-[10px] py-1">
      <span className={cx("h-[7px] w-[7px] flex-shrink-0 rounded-full", sync.isError ? "bg-danger" : "bg-positive")} />
      <span className="min-w-0 flex-1 truncate text-[12px] text-muted">{label}</span>
      {can("sync.run") && (
        <button
          type="button"
          aria-label="Synchroniser"
          disabled={sync.isPending}
          onClick={() => sync.mutate()}
          className={cx("text-[14px] leading-none text-accent", sync.isPending && "animate-spin")}
        >
          ⟳
        </button>
      )}
    </div>
  );
}

const SEVERITY_DOT = { INFO: "bg-accent", SUCCESS: "bg-positive", WARNING: "bg-warn", DANGER: "bg-danger" } as const;

/** Cloche des notifications : bouton 44 × 44 sans bordure (barre de navigation mobile, barre latérale PC). */
export function NotificationsMenu({ align = "right" }: { align?: "left" | "right" }) {
  const q = useNotifications();
  const read = useReadNotifications();
  const navigate = useNavigate();
  const unread = q.data?.unread ?? 0;
  return (
    <Dropdown
      width={330}
      align={align}
      trigger={(open, toggle) => (
        <button
          type="button"
          aria-label="Notifications"
          onClick={() => {
            toggle();
            if (!open && unread) read.mutate(undefined);
          }}
          className="relative -mr-[10px] flex h-11 w-11 items-center justify-center"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M6 8a6 6 0 1 1 12 0c0 7 3 9 3 9H3s3-2 3-9M10.3 21a1.94 1.94 0 0 0 3.4 0" />
          </svg>
          {unread > 0 && (
            <span className="absolute right-[7px] top-[7px] flex h-[17px] min-w-[17px] items-center justify-center rounded-full bg-ios-badge px-1 text-[11px] font-semibold text-white">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      )}
    >
      {(close) => (
        <div className="max-h-[420px] overflow-auto">
          <div className="px-2 pb-2 pt-1 text-[12.5px] font-bold text-[oklch(0.4_0.02_150)]">Notifications</div>
          {(q.data?.items ?? []).length === 0 && <div className="px-2 py-4 text-[12.5px] text-muted">Rien de nouveau.</div>}
          {(q.data?.items ?? []).map((n) => (
            <button
              key={n.id}
              type="button"
              onClick={() => {
                close();
                if (n.link) navigate(n.link);
              }}
              className="flex w-full items-start gap-[10px] rounded-[9px] px-2 py-[9px] text-left hover:bg-page"
            >
              <span className={cx("mt-[5px] h-[7px] w-[7px] flex-shrink-0 rounded-full", SEVERITY_DOT[n.severity])} />
              <span>
                <span className="block text-[12.5px] leading-[1.4] text-ink-2">
                  {n.title}
                  {n.body ? ` — ${n.body}` : ""}
                </span>
                <span className="mt-[2px] block text-[11px] text-faint">{relative(n.createdAt)}</span>
              </span>
            </button>
          ))}
        </div>
      )}
    </Dropdown>
  );
}

export async function logout() {
  await authClient.signOut().catch(() => {});
  queryClient.clear();
  window.location.href = "/login";
}
